import { insert, remove } from 'ramda';
import { Action } from 'typescript-fsa';
import * as dotProp from 'dot-prop-immutable';
import { partition, uniqBy } from 'lodash';

import { createReducer } from '@topconagriculture/frontend-utils';

import { selectActions } from '../../actions';
import { Feeding, FeedingPeriod } from './feeding.types';
import {
  getFeedingPeriodList,
  updateFeedingOrder,
  updateFeedingOrderLinkBaseFeeding,
  updateFeedingOrderLinkFeeding,
  updateFeedingPeriod,
} from './feeding.actions';
import { getFeedingPeriodBaseRationGroups } from './feeding.utils';

export type FeedingState = {
  readonly feedingPeriods: FeedingPeriod[];
};

export const initialState: FeedingState = {
  feedingPeriods: [],
};

const feedingReducer = createReducer(initialState, {
  [getFeedingPeriodList.done.type]: (state: FeedingState, action: Action<{ result: FeedingPeriod[] }>) => {
    const { result } = action.payload;

    return dotProp.set(
      state,
      'feedingPeriods',
      result.map((feedingPeriod) => ({
        ...feedingPeriod,
        baseRationGroups: getFeedingPeriodBaseRationGroups(feedingPeriod.feedings || [], feedingPeriod.feedingNo),
      })),
    );
  },
  [updateFeedingOrder.type]: (
    state: FeedingState,
    action: Action<{ feedingPeriodId: string; newPosition: number; feedingId: string }>,
  ) => {
    const { feedingPeriodId, feedingId, newPosition } = action.payload;
    const newState = state.feedingPeriods.map((feedingPeriod) =>
      feedingPeriod.id === feedingPeriodId
        ? updateFeedingOrderFn(feedingPeriod, feedingId, newPosition)
        : feedingPeriod,
    );
    return dotProp.set(state, 'feedingPeriods', newState);
  },
  [updateFeedingOrderLinkFeeding.type]: (
    state: FeedingState,
    action: ReturnType<typeof updateFeedingOrderLinkFeeding>,
  ) => {
    const { baseFeedingId, feedingId, feedingPeriodId } = action.payload;
    const newState = state.feedingPeriods.map((feedingPeriod) => {
      if (feedingPeriod.id === feedingPeriodId) {
        const feedings = feedingPeriod.feedings!;
        const baseFeedingIndex = feedings.findIndex((item) => item.id === baseFeedingId);
        const feedingIndex = feedings.findIndex((item) => item.id === feedingId);
        const newPosition = feedingIndex < baseFeedingIndex ? baseFeedingIndex : baseFeedingIndex + 1;
        return updateFeedingOrderFn(feedingPeriod, feedingId, newPosition);
      } else {
        return feedingPeriod;
      }
    });
    return dotProp.set(state, 'feedingPeriods', newState);
  },
  [updateFeedingOrderLinkBaseFeeding.type]: (
    state: FeedingState,
    action: ReturnType<typeof updateFeedingOrderLinkBaseFeeding>,
  ) => {
    const { feedingPeriodId, feedingGroup } = action.payload;
    const baseFeedingId = feedingGroup.feeding.feedingId;
    const baseRationId = feedingGroup.baseRationId;
    const newState = state.feedingPeriods.map((feedingPeriod) => {
      if (feedingPeriod.id === feedingPeriodId) {
        const feedings = feedingPeriod.feedings!;
        const feedingsPlusIds = feedingGroup.feedingsPlus.map((item) => item.feedingId);
        const [feedingsPlus, otherFeedings] = partition(feedings, (item) => feedingsPlusIds.includes(item.id));
        const baseFeedingIndex = otherFeedings.findIndex((item) => item.id === baseFeedingId);
        const sameBaseFeedings = feedings.filter((item) => item.rationId === baseRationId);
        const beforeBase = otherFeedings
          .slice(0, baseFeedingIndex + 1)
          .filter((item) => item.rationId !== baseRationId);
        const afterBaseFeedings = uniqBy(beforeBase.concat(sameBaseFeedings), 'id');
        const afterBase = otherFeedings.filter((item) => !afterBaseFeedings.includes(item));
        const newArray = uniqBy([...beforeBase, ...sameBaseFeedings, ...feedingsPlus, ...afterBase], 'id');
        return {
          ...feedingPeriod,
          feedings: newArray,
          baseRationGroups: getFeedingPeriodBaseRationGroups(newArray, feedingPeriod.feedingNo),
        };
      } else {
        return feedingPeriod;
      }
    });
    return dotProp.set(state, 'feedingPeriods', newState);
  },
  [selectActions.resetSelected.type]: (state: FeedingState) => {
    const newState = state.feedingPeriods.map((fp) => {
      const { feedings, ...feedingPeriod } = fp;
      return feedingPeriod;
    });
    return dotProp.set(state, 'feedingPeriods', newState);
  },
  [updateFeedingPeriod.done.type]: (state: FeedingState, action: ReturnType<typeof updateFeedingPeriod.done>) => {
    const { id, feedingNo, feedings } = action.payload.result;
    const newState = state.feedingPeriods.map((entity) =>
      entity.id === id
        ? {
            ...entity,
            ...action.payload.result,
            baseRationGroups: getFeedingPeriodBaseRationGroups(feedings || [], feedingNo),
          }
        : entity,
    );
    return dotProp.set(state, 'feedingPeriods', newState);
  },
});

const updateFeedingOrderFn = (feedingPeriod: FeedingPeriod, feedingId: string, newPosition: number): FeedingPeriod => {
  const currentArray = feedingPeriod.feedings || [];
  const currentItem = currentArray.find((feeding: Feeding) => feeding.id === feedingId)!;
  const findIndex = currentArray.findIndex((currentIndex) => currentIndex.id === feedingId);
  const slicedArray = remove(findIndex, 1, currentArray);
  const newArray = insert(newPosition, currentItem, slicedArray);
  return {
    ...feedingPeriod,
    feedings: newArray,
    baseRationGroups: getFeedingPeriodBaseRationGroups(newArray, feedingPeriod.feedingNo),
  };
};

export default feedingReducer;
